import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const juanLesPinsEn: ChauffeurPage = {
  slug: "juan-les-pins",
  title: "VTC & private chauffeur in Juan-les-Pins",
  subtitle:
    "Private chauffeur in Juan-les-Pins: Nice airport transfers, Jazz à Juan, beaches, nightlife and Cap d’Antibes, 7 days a week.",
  keywords: [
    "vtc juan-les-pins",
    "private chauffeur juan-les-pins",
    "juan-les-pins airport transfer",
  ],
  hero: {
    title: "VTC & private chauffeur in Juan-les-Pins",
    subtitle:
      "Nice airport transfers, Jazz à Juan, beaches, nights out and Cap d’Antibes — available 24/7.",
    keywords: [
      "VTC Juan-les-Pins",
      "Cap d’Antibes chauffeur",
      "Nice airport transfer",
      "Jazz à Juan",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Juan-les-Pins",
    badges: ["Available 24/7", "Flight tracking", "Bilingual chauffeurs"],
  },
  benefits: {
    heading: "Why a chauffeur in Juan-les-Pins",
    items: [
      {
        title: "Nice airport, door to door",
        description:
          "Nice airport around 25 minutes away via the A8, with flight tracking and a personal welcome.",
      },
      {
        title: "Nights out, no constraint",
        description:
          "Bars, clubs, casino and beaches: your chauffeur brings you back in complete peace of mind, no parking worry.",
      },
      {
        title: "Bilingual chauffeurs",
        description:
          "French/English, punctual and discreet, used to festival-goers and international guests.",
      },
      {
        title: "Local roots",
        description:
          "Based on the French Riviera, rated 5.0/5 from 38 Google reviews.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Juan-les-Pins",
    services: [
      {
        title: "Nice airport transfers",
        text: "Pick-up with flight tracking to and from Nice Côte d’Azur airport and Antibes station.",
      },
      {
        title: "Jazz à Juan & events",
        text: "Shuttles to the Pinède Gould during the festival, drop-off as close as possible and pick-up after the concert.",
      },
      {
        title: "Nights out & nightlife",
        text: "Bars, clubs, casino and restaurants: a dedicated chauffeur for your outings, return included.",
      },
      {
        title: "Cap d’Antibes",
        text: "Hôtel du Cap-Eden-Roc, villas and the coastal path, with a chauffeur for the Cap roads.",
      },
      {
        title: "Hire & long distance",
        text: "By the hour, the day or any distance: Cannes, Monaco, Saint-Tropez and Italy.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Pinède Gould",
        description: "Seafront pine grove, stage of the Jazz à Juan festival.",
      },
      {
        name: "Juan-les-Pins beaches",
        description: "Sandy beaches and beach clubs along the bay.",
      },
      {
        name: "Casino & centre",
        description: "Casino, bars and nightlife in the heart of the resort.",
      },
      {
        name: "Cap d’Antibes",
        description: "Hôtel du Cap-Eden-Roc, villas and the Tire-Poil path.",
      },
      {
        name: "Juan-les-Pins station",
        description: "Regional trains and access to Antibes within minutes.",
      },
      {
        name: "Nice airport",
        description: "About 25 minutes away via the A8 motorway.",
      },
    ],
  },
  generic: {
    heading: "A private chauffeur across Juan-les-Pins",
    subheading:
      "From the beaches to Jazz à Juan, premium service well into the night.",
    paragraphs: [
      "KS Rent Services handles your journeys with a private chauffeur in Juan-les-Pins and across the French Riviera: transfers from Nice airport (about 25 minutes), nights out, beaches and long-distance trips.",
      "A lively seaside resort of Antibes, Juan-les-Pins lives by day and by night: our bilingual chauffeurs know the Pinède Gould, the beaches, the casino and the Cap d’Antibes roads.",
      "During Jazz à Juan or for an evening in town, your chauffeur drops you as close as possible and collects you after the concert, with no parking or return constraint.",
    ],
    bullets: [
      "Flight tracking & personal welcome",
      "Peace-of-mind late return",
      "Festival shuttles & Cap d’Antibes",
    ],
  },
  process: {
    heading: "Book a chauffeur in Juan-les-Pins",
    steps: [
      "Share your route, timings and number of passengers.",
      "We confirm the right vehicle and the fixed fare.",
      "Your chauffeur tracks your flight and greets you on time.",
      "Live adjustments for traffic and any last-minute changes.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Juan-les-Pins"),
    {
      q: "How long is the transfer to Nice airport from Juan-les-Pins?",
      a: "Around 25 minutes via the A8 motorway in normal conditions. We run transfers 24/7 with flight tracking and a personal welcome.",
    },
    {
      q: "Do you run shuttles during Jazz à Juan?",
      a: "Yes, we serve the Pinède Gould during the festival, with drop-off as close as possible and pick-up after the concert.",
    },
    {
      q: "Can you bring us back after a night out in Juan-les-Pins?",
      a: "Yes, a dedicated chauffeur handles your bar, club and casino outings with the return included, at any hour.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English and used to international guests.",
    },
    {
      q: "Which vehicles do you offer in Juan-les-Pins?",
      a: "Mercedes E-Class and S-Class sedans, 7-seat van, SUV and VIP minibus, depending on the number of passengers and the occasion.",
    },
    {
      q: "Do you serve Cap d’Antibes?",
      a: "Yes, Hôtel du Cap-Eden-Roc, villas and the coastal path, with a chauffeur at ease on the narrow Cap roads.",
    },
  ],
  relatedLinks: [
    { label: "Cannes chauffeur rates", href: "/en/chauffeur/cannes" },
    { label: "Car service in Nice", href: "/en/chauffeur/nice" },
    { label: "Nice Airport chauffeur", href: "/en/chauffeur/airport" },
    {
      label: "French Riviera chauffeur service",
      href: "/en/chauffeur/french-riviera",
    },
  ],
  cta: {
    title: "Your private chauffeur in Juan-les-Pins",
    text: "Book your transfer or hourly hire — fast reply, 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Private chauffeur in Juan-les-Pins — car service & transfers",
    description:
      "Private car service in Juan-les-Pins: Nice airport transfer (~25 min), Jazz à Juan, beaches, nightlife and Cap d’Antibes. 5.0/5 from 38 reviews.",
    canonical: "/en/chauffeur/juan-les-pins",
  },
};

export default juanLesPinsEn;
